import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { ChevronRight, ChevronDown } from 'lucide-react';
import MuiTable, { DEFAULT_PAGE_SIZE } from '@root/components/table/table';
import useDebouncedValue from '@root/hooks/useDebouncedValue';
import useSortData from '@root/hooks/useSortData';
import useEnhancedQuery from '@root/query/useEnhancedQuery';
import HeaderBar from '@root/components/HeaderBarNew';
import agent from '@root/agent';
import { malwareColumns } from './column';
import ClamavFleetDetailPanel from './ClamavFleetDetailPanel';

const TABLE_COMPONENTS = {
  DetailPanelExpandIcon: () => <ChevronRight size={16} style={{ color: '#6b7280' }} />,
  DetailPanelCollapseIcon: () => <ChevronDown size={16} style={{ color: '#6b7280' }} />,
};

const AllScansTab = ({ environments = [], enabled = false, tabType = 'malware' }) => {
  const [searchString, setSearchString] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const { sortData, handleSortModelChange } = useSortData();
  const debouncedSearchString = useDebouncedValue(searchString, 750);

  const environmentIds = useMemo(() => environments.map(e => e.id), [environments]);

  const {
    data: listResponse,
    isLoading,
    isFetching,
  } = useEnhancedQuery(
    ['get_k8s_scan_history_list', `${environmentIds}`, currentPage, pageSize, debouncedSearchString, sortData],
    () =>
      agent.Kubernetes.get_k8s_scan_history_list({
        environments: environmentIds,
        clusters: [],
        search_query: debouncedSearchString,
        page_number: currentPage,
        rows_per_page: pageSize,
        export: false,
        sort: sortData?.length
          ? sortData.map(({ key, value }) => ({ field: key, order: value }))
          : [{ field: 'scan_timestamp', order: 'desc' }],
      }),
    {
      enabled,
      staleTime: 30000,
      cacheTime: 300000,
      keepPreviousData: true,
    },
  );

  const tableRows = useMemo(() => {
    const items = listResponse?.data ?? [];
    return items.map((item, idx) => ({
      ...item,
      id: item.scan_id ?? `scan-${idx}`,
    }));
  }, [listResponse]);

  const tableColumns = useMemo(() => {
    return malwareColumns;
  }, []);

  const handleSearchChange = useCallback(e => {
    setSearchString(e.target.value);
  }, []);

  const handlePageChange = useCallback(
    page => {
      if (isFetching) return;
      setCurrentPage(page);
    },
    [isFetching],
  );

  const handlePageSizeChange = useCallback(_pageSize => {
    setCurrentPage(1);
    setPageSize(_pageSize);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchString]);

  return (
    <Box display="flex" flexDirection="column" flex={1}>
      <HeaderBar
        searchBar
        searchBarValue={searchString}
        setSearchTerm={setSearchString}
        searchBarOnChange={handleSearchChange}
        onSearchBarSubmit={e => e.preventDefault()}
        marginBottom={'15px'}
        exportButton={false}
      />
      <MuiTable
        rows={tableRows}
        columns={tableColumns}
        LoaderState={isLoading || isFetching}
        page={currentPage}
        total_size={listResponse?.pagination?.total ?? 0}
        pageSize={pageSize}
        rowHeight={50}
        isDynamicHeight={true}
        paginationMode={'server'}
        handleSortModelChange={handleSortModelChange}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        disableDynamicColumn={true}
        noDataMessage="No scan history available"
        components={TABLE_COMPONENTS}
        expandRow={tabType === 'malware' ? row => <ClamavFleetDetailPanel row={row} /> : undefined}
      />
    </Box>
  );
};

export default AllScansTab;
